import { Ref, useState } from "react";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";

import type { PasswordCondition } from "client/accounts/types";

import ConditionCheck from "../display/ConditonCheck";
import BaseInput from "./BaseInput";

import styles from "../../styles/PasswordInput.module.less";

export type PasswordInputProps = {
  /** The string value representing the password as the user inputs characters */
  value: string;
  /** Callback triggered when the value changes */
  onChange: (value: string) => void;
  /** Error messages to be displayed under the input */
  errors?: string[];
  /** The text label to display for the input */
  label?: string;
  /** Any conditions the value should pass to be considered valid */
  passwordConditions: PasswordCondition[];
  ref?: Ref<HTMLInputElement>;
};

/**
 * Text input which hides user inputted value and has checkboxes based on provided
 * password condition checks.
 */
const PasswordInput = ({
  value,
  onChange,
  errors,
  label = "Password",
  passwordConditions,
  ref,
}: PasswordInputProps) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className={styles.container}>
      <BaseInput
        value={value}
        onChange={onChange}
        errors={errors}
        renderInputDisplay={({ onChange: baseOnChange, ...inputProps }) => (
          <>
            <BaseInput.AnimationContainer label={label}>
              <input
                {...inputProps}
                placeholder="" // Disable placeholders since the label sticks there when value is empty
                ref={ref}
                type={isVisible ? "text" : "password"}
                onChange={(event) => baseOnChange(event.target.value)}
              />
            </BaseInput.AnimationContainer>
            <button
              type="button"
              onClick={() => setIsVisible(!isVisible)}
              className={styles.button}
              tabIndex={-1}
            >
              <Icon
                icon={isVisible ? faEye : faEyeSlash}
                className={styles.icon}
                fixedWidth={true}
              />
            </button>
          </>
        )}
      />
      {!!passwordConditions.length && (
        <div className={styles.passwordConditions}>
          {passwordConditions.map(({ check, label: checkLabel }) => (
            <ConditionCheck key={checkLabel} isMet={check(value)}>
              {checkLabel}
            </ConditionCheck>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
